import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDuplicateBriefingMutation } from "../../store/apis/briefingApi";
import { useNavigate } from "react-router-dom";

interface DuplicateBriefingDialogProps {
  open: boolean;
  onClose: () => void;
  briefingId: string;
}

const DuplicateBriefingDialog: React.FC<DuplicateBriefingDialogProps> = ({
  open,
  onClose,
  briefingId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [duplicateBriefing, { isLoading }] = useDuplicateBriefingMutation();

  const handleDuplicate = async () => {
    try {
      const response = await duplicateBriefing({
        id: briefingId,
        payload: {},
      }).unwrap();

      if (response.data?.id) {
        onClose();
        navigate(`/briefings/${response.data.id}`);
      }
    } catch (error) {
      console.error("Failed to duplicate briefing:", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("briefingsView.duplicateTitle", "Duplicate Briefing")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body1">
            {t(
              "briefingsView.duplicateStandardDesc",
              "Creates an exact duplicate of this briefing and its associated campaigns. Dates and budgets will not be copied."
            )}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isLoading}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleDuplicate}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {t("common.duplicate", "Duplicate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateBriefingDialog;
